import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import RulesModal from './RulesModal'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { leaderboardAPI, getApiBaseUrlWithoutApi } from '../config/api'
import 'primeicons/primeicons.css'

export default function LeaderboardSection() {
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false)
  const [topPlayers, setTopPlayers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [totalUsers, setTotalUsers] = useState<number | null>(null)
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()

  // Monthly history states
  const [monthlyHistoryList, setMonthlyHistoryList] = useState<{ year: number; month: number; label: string }[]>([])
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | null>(null)
  const [monthlyPlayers, setMonthlyPlayers] = useState<any[]>([])
  const [loadingMonthly, setLoadingMonthly] = useState(false)
  const [loadingMonthlyList, setLoadingMonthlyList] = useState(true)

  useEffect(() => {
    const fetchTopPlayers = async () => {
      try {
        setLoading(true)
        const data = await leaderboardAPI.getTopPlayers()
        setTopPlayers(data.slice(0, 5))
      } catch {
        setTopPlayers([])
      } finally {
        setLoading(false)
      }
    }

    const fetchTotalUsers = async () => {
      try {
        const data = await leaderboardAPI.getTotalUsers()
        setTotalUsers(data.total_users)
      } catch {
        // ignore
      }
    }

    fetchTopPlayers()
    fetchTotalUsers()

    // Fetch danh sách tháng có lịch sử (chỉ khi đã login)
    if (isAuthenticated) {
      const fetchMonthlyList = async () => {
        try {
          setLoadingMonthlyList(true)
          const data = await leaderboardAPI.getMonthlyHistoryList()
          setMonthlyHistoryList(data)
          if (data.length > 0) {
            setSelectedMonth({ year: data[0].year, month: data[0].month })
          } else {
            setLoadingMonthlyList(false)
          }
        } catch {
          setMonthlyHistoryList([])
          setLoadingMonthlyList(false)
        }
      }
      fetchMonthlyList()
    }
  }, [isAuthenticated])

  // Fetch top 10 khi chọn tháng
  useEffect(() => {
    if (!selectedMonth || !isAuthenticated) return
    const fetchMonthlyData = async () => {
      try {
        setLoadingMonthly(true)
        const API_BASE_URL = getApiBaseUrlWithoutApi()
        const data = await leaderboardAPI.getMonthlyHistory(selectedMonth.year, selectedMonth.month)
        const processed = data.map((p: any) => {
          const np = { ...p }
          if (np.avatar && np.avatar.startsWith('/media/')) {
            np.avatar = `${API_BASE_URL}${np.avatar}`
          } else if (np.avatar && !np.avatar.startsWith('http') && !np.avatar.startsWith('/images/')) {
            np.avatar = `${API_BASE_URL}${np.avatar}`
          }
          return np
        })
        setMonthlyPlayers(processed)
      } catch {
        setMonthlyPlayers([])
      } finally {
        setLoadingMonthly(false)
        setLoadingMonthlyList(false)
      }
    }
    fetchMonthlyData()
  }, [selectedMonth, isAuthenticated])

  return (
    <section className="py-10 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-relaxed text-white">
            TOP 5 BẢNG XẾP HẠNG
          </h2>
          <p className="text-sm md:text-base text-white">
            Thi đua luyện tập - vươn lên mỗi ngày
          </p>
        </div>

        {/* Top 5 Leaderboard */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className={`${theme === 'dark' ? 'bg-black/60' : 'bg-gray-200/90'} border ${theme === 'dark' ? 'border-gold-500/40' : 'border-gray-900'} rounded-xl p-5 space-y-3`}>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-400 mx-auto mb-4"></div>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải...</p>
              </div>
            ) : topPlayers.length === 0 ? (
              <div className="text-center py-8">
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Chưa có dữ liệu xếp hạng</p>
              </div>
            ) : (
              topPlayers.map((player, index) => {
                const isTop3 = index < 3

                return (
                  <div
                    key={player.id || player.rank || index}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg
                ${isTop3
                        ? theme === 'dark'
                          ? 'bg-gradient-to-r from-gold-600/30 to-transparent border border-gold-400'
                          : 'bg-gradient-to-r from-gold-600/30 to-transparent border border-gray-900'
                        : theme === 'dark'
                          ? 'bg-white/5 border border-white/10'
                          : 'bg-gray-200/50 border border-gray-900'
                      }`}
                  >
                    {/* Left */}
                    <div className="flex items-center gap-4">
                      <div className="text-2xl font-bold w-8 text-center">
                        {index === 0
                          ? '🥇'
                          : index === 1
                            ? '🥈'
                            : index === 2
                              ? '🥉'
                              : `${index + 1}.`}
                      </div>

                      <div>
                        <h3 className={`font-semibold truncate max-w-[180px] ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {player.name}
                        </h3>
                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          🔥 {player.streak || 0} ngày
                        </span>
                      </div>
                    </div>

                    {/* Right */}
                    <div className="text-right flex items-center gap-1">
                      <div className="text-lg font-bold text-gold-400">
                        {player.points || 0}
                      </div>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>điểm</span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mb-12">
          {isAuthenticated ? (
            <>
              <p className="text-lg md:text-xl font-semibold mb-6 text-white">
                Bạn có muốn lên TOP không?
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/leaderboard"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gold-gradient text-white font-bold rounded-full hover:scale-105 transition"
                >
                  <i className="pi pi-chart-line"></i>
                  Xem bảng xếp hạng đầy đủ
                </Link>

                <button
                  onClick={() => setIsRulesModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gold-500 font-bold rounded-full hover:scale-105 transition text-white hover:bg-gold-500/10"
                >
                  <i className="pi pi-info-circle"></i>
                  Luật chơi
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-white">
                TOP 1 Không Dành Cho Người Thiếu Kỷ Luật
              </h2>
              <p className="text-base md:text-lg mb-6 text-white">
                Tham gia ngay để biết kỷ luật của bạn đưa bạn đi đến đâu.
              </p>

              <div className="flex flex-col items-center gap-3">
                <Link
                  to="/login"
                  className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gold-gradient text-white font-bold text-base md:text-lg rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-gold-500/50"
                >
                  <i className="pi pi-chart-line text-xl group-hover:rotate-12 transition-transform duration-300"></i>
                  <span>THAM GIA NGAY</span>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <i className="pi pi-arrow-up-right text-xs text-gold-600"></i>
                  </div>
                </Link>
                {totalUsers !== null && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                    <i className="pi pi-users text-gold-400"></i>
                    <span className="text-sm font-semibold text-white">
                      <span className="text-gold-400 font-bold">{totalUsers.toLocaleString('vi-VN')}</span> người đã tham gia
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Rules Modal */}
        {isAuthenticated && (
          <RulesModal
            isOpen={isRulesModalOpen}
            onClose={() => setIsRulesModalOpen(false)}
          />
        )}

        {/* Features Section (Unauthenticated) or Prizes Section (Authenticated) */}
        {isAuthenticated ? (
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-3xl font-bold inline-block text-white">
                GIẢI THƯỞNG TOP THÁNG
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* TOP 1 */}
              <div className={`rounded-xl border border-gold-400 ${theme === 'dark' ? 'bg-gold-600/20' : 'bg-gray-200/90'} p-6`}>
                <h4 className={`text-xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  🏆 TOP 1 THÁNG
                </h4>
                <ul className={`space-y-2 text-sm text-center list-none ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  <li>Coach online 1-1 cùng HLV</li>
                  <li>Phân tích & lộ trình cá nhân</li>
                </ul>
              </div>

              {/* TOP 2 */}
              <div className={`rounded-xl border ${theme === 'dark' ? 'border-gray-500 bg-gray-700/30' : 'border-gray-400 bg-gray-200/90'} p-6`}>
                <h4 className={`text-xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  🥈 TOP 2 THÁNG
                </h4>
                <ul className={`space-y-2 text-sm text-center list-none ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  <li>Vé trải nghiệm lớp cộng đồng</li>
                </ul>
              </div>

              {/* TOP 3 */}
              <div className={`rounded-xl border ${theme === 'dark' ? 'border-amber-600 bg-amber-800/30' : 'border-amber-500 bg-gray-200/90'} p-6`}>
                <h4 className={`text-xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  🥉 TOP 3 THÁNG
                </h4>
                <ul className={`space-y-2 text-sm text-center list-none ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  <li>01 tháng membership miễn phí</li>
                </ul>
              </div>
            </div>

            {/* 🏅 Bảng Vinh Danh Theo Tháng */}
            <div className="mt-12">
              <div className="text-center mb-6">
                <h3 className="text-2xl md:text-3xl font-bold inline-block text-white">
                  TOP 10 THEO THÁNG
                </h3>
                <p className="text-sm mt-2 text-white/80">
                  Vinh danh những cầu thủ xuất sắc qua từng tháng
                </p>
              </div>

              {loadingMonthlyList ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-400 mx-auto mb-4"></div>
                  <p className="text-gray-400 text-sm">Đang tải...</p>
                </div>
              ) : monthlyHistoryList.length === 0 ? (
                <div className={`${theme === 'dark' ? 'bg-black/60' : 'bg-gray-200/90'} border ${theme === 'dark' ? 'border-gold-500/40' : 'border-gray-900'} rounded-xl p-5 text-center py-10`}>
                  <div className="text-4xl mb-3">📭</div>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Chưa có dữ liệu lịch sử tháng nào</p>
                  <p className="text-gray-500 text-xs mt-1">Dữ liệu sẽ được lưu sau lần reset bảng xếp hạng đầu tiên</p>
                </div>
              ) : (
                <>
                  {/* Tabs chọn tháng - scroll ngang */}
                  <div className="flex gap-2 overflow-x-auto pb-2 mb-5" style={{ scrollbarWidth: 'none' }}>
                    {monthlyHistoryList.map((item) => {
                      const isActive = selectedMonth?.year === item.year && selectedMonth?.month === item.month
                      return (
                        <button
                          key={`${item.year}-${item.month}`}
                          onClick={() => setSelectedMonth({ year: item.year, month: item.month })}
                          className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-bold transition-all duration-200 border-2 ${isActive
                              ? 'bg-gold-gradient text-white border-transparent shadow-lg scale-105'
                              : theme === 'dark'
                                ? 'bg-black/40 text-gray-300 border-gold-500/30 hover:border-gold-400 hover:text-white'
                                : 'bg-white/60 text-gray-700 border-gold-400/50 hover:border-gold-500'
                            }`}
                        >
                          {item.label}
                        </button>
                      )
                    })}
                  </div>

                  {/* Bảng top 10 */}
                  <div className={`${theme === 'dark' ? 'bg-black/60' : 'bg-gray-200/90'} border ${theme === 'dark' ? 'border-gold-500/40' : 'border-gray-900'} rounded-xl overflow-hidden`}>
                    {/* Bảng header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gold-500/20 bg-gold-500/10">
                      <span className="text-gold-400 font-bold text-sm">
                        🏆 {monthlyHistoryList.find(m => m.year === selectedMonth?.year && m.month === selectedMonth?.month)?.label}
                      </span>
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Top 10 cầu thủ</span>
                    </div>

                    {loadingMonthly ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gold-400 mx-auto mb-3"></div>
                        <p className="text-gray-400 text-sm">Đang tải...</p>
                      </div>
                    ) : monthlyPlayers.length === 0 ? (
                      <p className={`text-center py-8 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Không có dữ liệu cho tháng này</p>
                    ) : (
                      <div className="space-y-0">
                        {monthlyPlayers.map((player: any, index: number) => {
                          const isTop3 = index < 3
                          const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : null
                          return (
                            <div
                              key={player.id ?? index}
                              className={`flex items-center justify-between px-4 py-2.5 border-b last:border-0 ${isTop3
                                  ? theme === 'dark'
                                    ? 'bg-gradient-to-r from-gold-600/30 to-transparent border-gold-400/30'
                                    : 'bg-gradient-to-r from-gold-600/20 to-transparent border-gray-200'
                                  : theme === 'dark'
                                    ? 'bg-white/5 border-white/10 hover:bg-white/10'
                                    : 'bg-gray-200/50 border-gray-300 hover:bg-gray-100'
                                } transition-colors`}
                            >
                              {/* LEFT: rank + avatar + name */}
                              <div className="flex items-center gap-3">
                                <div className="text-lg font-bold w-7 text-center flex-shrink-0">
                                  {medal
                                    ? <span>{medal}</span>
                                    : <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{index + 1}.</span>
                                  }
                                </div>
                                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gold-400/40 flex-shrink-0">
                                  {player.avatar ? (
                                    <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gold-700/40 flex items-center justify-center">
                                      <span className="text-gold-300 font-bold text-xs">{(player.name || 'U').charAt(0).toUpperCase()}</span>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <h3 className={`font-semibold truncate max-w-[150px] text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {player.name || 'N/A'}
                                  </h3>
                                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {player.session || 0} buổi điểm danh
                                  </p>
                                </div>
                              </div>
                              {/* RIGHT: points */}
                              <div className="text-right flex items-center gap-1 flex-shrink-0">
                                <div className={`text-base font-bold ${isTop3 ? 'text-gold-400' : theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {player.points || 0}
                                </div>
                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>điểm</span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Card 1: Huấn luyện cá nhân hóa */}
              <div className={`rounded-xl border ${theme === 'dark' ? 'border-gold-500/40 bg-black/60' : 'border-gold-400 bg-white'} p-6 transition-all duration-300`}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gold-gradient flex items-center justify-center mb-4">
                    <i className="pi pi-user-edit text-2xl text-black"></i>
                  </div>
                  <h3 className={`text-lg md:text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Huấn luyện cá nhân hóa
                  </h3>
                  <p className={`text-sm md:text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Phương pháp tập luyện được thiết kế riêng cho từng học viên
                  </p>
                </div>
              </div>

              {/* Card 2: Cải thiện kỹ năng nhanh chóng */}
              <div className={`rounded-xl border ${theme === 'dark' ? 'border-gold-500/40 bg-black/60' : 'border-gold-400 bg-white'} p-6 transition-all duration-300`}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gold-gradient flex items-center justify-center mb-4">
                    <i className="pi pi-bolt text-2xl text-black"></i>
                  </div>
                  <h3 className={`text-lg md:text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Cải thiện kỹ năng nhanh chóng
                  </h3>
                  <p className={`text-sm md:text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Kỹ thuật thực chiến giúp bạn tiến bộ sau mỗi buổi tập
                  </p>
                </div>
              </div>

              {/* Card 3: Phát triển toàn diện */}
              <div className={`rounded-xl border ${theme === 'dark' ? 'border-gold-500/40 bg-black/60' : 'border-gold-400 bg-white'} p-6 transition-all duration-300`}>
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gold-gradient flex items-center justify-center mb-4">
                    <i className="pi pi-th-large text-2xl text-black"></i>
                  </div>
                  <h3 className={`text-lg md:text-xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Phát triển toàn diện
                  </h3>
                  <p className={`text-sm md:text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Rèn luyện cả thể lực và tư duy chiến thuật bóng đá
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

